import React, { useState } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import { sharedClasses } from './sharedClasses';
import { BODY_TEXT, DIVIDER } from '../../../ThemeContext/constants';
import { theme } from '../../../ThemeContext/ThemeObject';

const FormRadioButtonGroup = ({
  options,
  onChange,
  defaultValue,
  groupLabel,
  directionRow,
  innerRef,
  error,
  required,
  color,
  draggable,
  clearable = false,
  labelFontSize = '14px'
}: {
  options: { label: React.ReactNode; value: string | number }[];
  onChange: (value: string | number) => void;
  directionRow?: boolean;
  defaultValue?: string | number;
  groupLabel?: string;
  innerRef?: React.Ref<HTMLDivElement>;
  error?: string;
  color?: string;
  required?: boolean;
  draggable?: boolean;
  clearable?: boolean;
  labelFontSize?: string;
}) => {
  const [value, setValue] = useState(defaultValue ?? '');
  const [trackedDefault, setTrackedDefault] = useState(defaultValue);
  if (defaultValue !== trackedDefault) {
    setTrackedDefault(defaultValue);
    setValue(defaultValue ?? '');
  }

  const onInternalChange = (nextValue: string) => {
    setValue(nextValue);
    if (typeof onChange === 'function') {
      onChange(nextValue);
    }
  };

  const radioStyle = {
    color: DIVIDER,
    '&.Mui-checked': {
      color: theme.palette.action.main
    },
    '& .MuiSvgIcon-root': {
      fontSize: '22px'
    }
  };

  const labelStyle = {
    '& .MuiTypography-root': {
      color: color ?? `${BODY_TEXT} !important`,
      fontSize: '14px',
      marginLeft: '2px',
      fontFamily: 'Source Sans Pro, sans-serif'
    }
  };

  return (
    <FormControl>
      {groupLabel ? (
        <FormLabel
          data-testid={groupLabel?.toLowerCase().split(' ').join('-')}
          id={groupLabel?.toLowerCase().split(' ').join('-')}
          sx={{ ...sharedClasses.inputLabel, fontSize: labelFontSize }}
        >
          {groupLabel}
          {required ? <span>*</span> : null}
        </FormLabel>
      ) : null}
      <RadioGroup
        ref={innerRef}
        aria-labelledby={groupLabel?.toLowerCase().split(' ').join('-')}
        name="radio-buttons-group"
        onChange={(e) => onInternalChange(e.target.value)}
        sx={{ padding: '8px 0px 10px 12px', flexDirection: directionRow ? 'row' : 'column' }}
        value={value}
      >
        {options.map((option) => {
          const optionValue = draggable ? 'disabled' : option.value;
          const optionId =
            typeof option.label === 'string'
              ? option.label.toLowerCase().split(' ').join('-')
              : String(option.value);
          return (
            <FormControlLabel
              key={`${optionId}-${String(option.value)}`}
              control={<Radio sx={radioStyle} />}
              data-testid={optionId}
              disabled={!!draggable}
              id={optionId}
              label={option.label}
              sx={labelStyle}
              value={optionValue}
              onClick={(event) => {
                if (clearable && String(value) === String(optionValue)) {
                  event.preventDefault();
                  setValue('');
                  onChange('');
                }
              }}
            />
          );
        })}
        {error ? <Box sx={sharedClasses.errorBoxCheckbox}>{error}</Box> : null}
      </RadioGroup>
    </FormControl>
  );
};

export default FormRadioButtonGroup;
